import { useState, useRef, useEffect } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Sparkles, X, Send, Check, Ban } from "lucide-react";

export default function AgentWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const sessionRef = useRef(localStorage.getItem("nexa_agent_session") || null);
  const bottomRef = useRef(null);

  const openPanel = async () => {
    setOpen(true);
    if (sessionRef.current && messages.length === 0) {
      try {
        const res = await api.get(`/agent/sessions/${sessionRef.current}/messages`);
        setMessages(res.data.map((m) => ({ role: m.role, content: m.content })));
      } catch {
        // session expirée ou invalide : on repart sur une nouvelle
        sessionRef.current = null;
        localStorage.removeItem("nexa_agent_session");
      }
    }
  };

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length, open]);

  if (!user) return null;

  const send = async (e) => {
    e?.preventDefault();
    if (!input.trim() || sending) return;
    const text = input.trim();
    setInput("");
    setError("");
    setSending(true);
    setMessages((m) => [...m, { role: "user", content: text }]);
    try {
      const res = await api.post("/agent/chat", { message: text, session_id: sessionRef.current });
      sessionRef.current = res.data.session_id;
      localStorage.setItem("nexa_agent_session", res.data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: res.data.reply, suggestions: res.data.suggestions, action: res.data.action }]);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const decide = async (actionId, decision) => {
    setError("");
    try {
      const res = await api.post(`/agent/actions/${actionId}/decide`, { decision });
      setMessages((m) =>
        m.map((msg) =>
          msg.action?.action_id === actionId ? { ...msg, action: { ...msg.action, status: res.data.status, result: res.data.result } } : msg
        )
      );
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  if (!open) {
    return (
      <button
        data-testid="agent-open-button"
        onClick={openPanel}
        title="Assistant NEXA"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-brand-navy text-brand-turquoise shadow-lg hover:scale-105 transition-transform flex items-center justify-center"
      >
        <Sparkles className="w-6 h-6" />
      </button>
    );
  }

  return (
    <div data-testid="agent-panel" className="fixed bottom-6 right-6 z-50 w-[92vw] max-w-sm bg-white rounded-2xl border border-slate-200 shadow-2xl flex flex-col overflow-hidden">
      <div className="bg-brand-navy px-4 py-3 flex items-center justify-between">
        <p className="text-white text-sm font-semibold flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-brand-turquoise" />
          Assistant NEXA
        </p>
        <button data-testid="agent-close-button" onClick={() => setOpen(false)} className="text-white/60 hover:text-white">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div data-testid="agent-messages" className="flex-1 min-h-[240px] max-h-[50vh] overflow-y-auto p-4 space-y-3">
        {messages.length === 0 && (
          <p data-testid="agent-welcome" className="text-xs text-slate-400 text-center py-8">
            Posez-moi une question sur vos objectifs, candidatures ou offres. Je peux suggérer, rédiger des brouillons — et proposer des actions que vous seul approuvez.
          </p>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 ${m.role === "user" ? "bg-brand-navy text-white" : "bg-slate-100 text-brand-navy"}`}>
              <p className="text-sm whitespace-pre-line">{m.content}</p>
              {m.suggestions?.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {m.suggestions.map((s, j) => (
                    <li key={j} className="text-xs text-brand-blue bg-white rounded-lg px-2.5 py-1.5 border border-slate-200">{s}</li>
                  ))}
                </ul>
              )}
              {m.action && (
                <div data-testid={`agent-action-${m.action.action_id}`} className="mt-2 bg-white border border-brand-orange/40 rounded-lg p-3">
                  <p className="text-xs font-semibold text-brand-orange uppercase tracking-wide mb-1">Action proposée — votre approbation est requise</p>
                  <p className="text-sm text-brand-navy">{m.action.label}</p>
                  {m.action.status === "pending" ? (
                    <div className="flex gap-2 mt-2">
                      <button
                        data-testid={`agent-approve-${m.action.action_id}`}
                        onClick={() => decide(m.action.action_id, "approved")}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-brand-turquoise rounded-lg px-3 py-1.5 hover:bg-brand-navy"
                      >
                        <Check className="w-3.5 h-3.5" /> Approuver
                      </button>
                      <button
                        data-testid={`agent-reject-${m.action.action_id}`}
                        onClick={() => decide(m.action.action_id, "rejected")}
                        className="inline-flex items-center gap-1 text-xs font-medium text-red-600 border border-red-200 rounded-lg px-3 py-1.5 hover:bg-red-50"
                      >
                        <Ban className="w-3.5 h-3.5" /> Rejeter
                      </button>
                    </div>
                  ) : (
                    <p data-testid={`agent-action-result-${m.action.action_id}`} className="text-xs mt-2 font-medium text-slate-500">
                      {m.action.status === "executed" ? `Exécutée : ${m.action.result}` : "Rejetée"}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
        {sending && <p data-testid="agent-thinking" className="text-xs text-slate-400">L'assistant réfléchit…</p>}
        <div ref={bottomRef} />
      </div>

      {error && <p data-testid="agent-error" className="text-xs text-red-600 px-4 pb-1">{error}</p>}

      <form onSubmit={send} className="flex gap-2 p-3 border-t border-slate-100">
        <input
          data-testid="agent-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Écrivez à l'assistant…"
          maxLength={1000}
          className="flex-1 border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-turquoise"
        />
        <button
          data-testid="agent-send-button"
          type="submit"
          disabled={sending || !input.trim()}
          className="bg-brand-turquoise text-white rounded-lg px-4 py-2.5 hover:bg-brand-navy transition-colors disabled:opacity-40"
        >
          <Send className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
}
